import React, { useEffect, useState } from 'react';
import { Star, RefreshCw, Unplug, MapPin } from 'lucide-react';
import { RCard } from './restui';
import { isRestDemo, DEMO_GBP_STATUS } from '../../demo/restDemo';

/* Google Business Profile — real OAuth connection card. */
const API = process.env.REACT_APP_BACKEND_URL;

export function GoogleBusinessCard({ isEs, me }) {
  const [st, setSt] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [locations, setLocations] = useState(null);
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const isDemo = me?.role === 'demo';

  const load = async () => {
    if (isRestDemo()) { setSt(DEMO_GBP_STATUS); return; }
    const r = await fetch(`${API}/api/rest/google/status`, { credentials: 'include' });
    if (r.ok) setSt(await r.json());
  };
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    if (q.get('google') === 'error') setErr(q.get('reason') || 'error');
  }, []);

  const connect = async () => {
    setBusy('connect'); setErr('');
    const res = await fetch(`${API}/api/rest/google/start?product=gbp`, { credentials: 'include' });
    setBusy('');
    if (res.ok) { const j = await res.json(); window.location.href = j.url; }
    else setErr((await res.json()).detail || 'error');
  };
  const loadAccounts = async () => {
    setBusy('accounts'); setErr('');
    const r = await fetch(`${API}/api/rest/google/gbp/accounts`, { credentials: 'include' });
    setBusy('');
    if (r.ok) setAccounts((await r.json()).accounts);
    else setErr((await r.json()).detail === 'pending_google_approval' ? 'approval' : 'accounts_failed');
  };
  const loadLocations = async (acc) => {
    setAccount(acc); setBusy('locations');
    const r = await fetch(`${API}/api/rest/google/gbp/locations?account=${encodeURIComponent(acc)}`, { credentials: 'include' });
    setBusy('');
    if (r.ok) setLocations((await r.json()).locations);
    else setErr('locations_failed');
  };
  const select = async (loc) => {
    setBusy('sync');
    const r = await fetch(`${API}/api/rest/google/gbp/select`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account, location: loc.name, location_title: loc.title }),
    });
    setBusy(''); setAccounts(null); setLocations(null);
    if (!r.ok) setErr('sync_failed');
    load();
  };
  const syncNow = async () => {
    setBusy('sync');
    const r = await fetch(`${API}/api/rest/google/gbp/sync`, { method: 'POST', credentials: 'include' });
    setBusy('');
    if (!r.ok) setErr((await r.json()).detail === 'pending_google_approval' ? 'approval' : 'sync_failed');
    load();
  };
  const disconnect = async () => {
    setBusy('disc');
    await fetch(`${API}/api/rest/google/disconnect`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product: 'gbp' }),
    });
    setBusy(''); load();
  };

  const gbp = st?.gbp || { status: 'not_connected' };
  const snap = st?.gbp_snapshot;
  const connected = gbp.status === 'connected';

  return (
    <RCard className="p-6 mb-6" testid="gbp-card">
      <div className="flex flex-wrap items-center gap-4">
        <img src="https://www.gstatic.com/images/branding/product/2x/google_my_business_64dp.png" alt="" className="w-9 h-9" onError={(e) => { e.target.style.display = 'none'; }} />
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-[15px] font-semibold text-gray-900">
            Google Business Profile
            <span className={`ml-2 text-[10.5px] font-bold uppercase px-2 py-0.5 rounded-full ${connected ? 'bg-emerald-50 text-emerald-600' : gbp.status === 'select_location' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`} data-testid="gbp-status">
              {connected ? (isEs ? '● Conectado' : '● Connected') : gbp.status === 'select_location' ? (isEs ? 'Elige ubicación' : 'Pick location') : gbp.status === 'reauth_required' ? (isEs ? 'Reconectar' : 'Reconnect') : (isEs ? 'No conectado' : 'Not connected')}
            </span>
          </h2>
          <p className="text-[12.5px] text-gray-500">
            {connected
              ? `${gbp.google_email} · ${gbp.location || ''}${gbp.connected_by ? ` · ${isEs ? 'conectado por' : 'connected by'} ${gbp.connected_by}` : ''}${gbp.connected_at ? ` · ${gbp.connected_at.slice(0, 10)}` : ''}${gbp.last_sync ? ` · ${isEs ? 'sync' : 'synced'} ${gbp.last_sync.slice(0, 16).replace('T', ' ')}` : ''}`
              : (isEs ? 'Tu rating, reseñas y métricas reales de Google — directo a tu Intelligence.' : 'Your real Google rating, reviews and metrics — straight into Intelligence.')}
          </p>
          {connected && gbp.last_sync_error && (
            <p className="text-[11.5px] text-amber-600 font-semibold" data-testid="gbp-last-error">⚠ {isEs ? 'Último error' : 'Last error'}: {gbp.last_sync_error.slice(0, 120)}</p>
          )}
        </div>
        {connected && snap?.rating && (
          <div className="text-center" data-testid="gbp-rating">
            <p className="text-[22px] font-bold text-gray-900 flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> {snap.rating}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{snap.review_count} {isEs ? 'reseñas' : 'reviews'}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!connected && gbp.status !== 'select_location' && !isDemo && (
            <button onClick={connect} disabled={!!busy} data-testid="gbp-connect-btn"
              className="rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[12.5px] px-5 py-2 transition-opacity disabled:opacity-50">
              {busy === 'connect' ? '…' : (isEs ? 'Conectar con Google' : 'Connect with Google')}
            </button>
          )}
          {gbp.status === 'select_location' && !accounts && (
            <button onClick={loadAccounts} disabled={!!busy} data-testid="gbp-pick-btn"
              className="rounded-full bg-[var(--rest-accent)] text-white font-bold text-[12.5px] px-5 py-2 disabled:opacity-50">
              {busy === 'accounts' ? '…' : (isEs ? 'Elegir mi negocio →' : 'Pick my business →')}
            </button>
          )}
          {connected && !isDemo && (
            <>
              <button onClick={syncNow} disabled={!!busy} data-testid="gbp-sync-btn" title="Sync now"
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[var(--rest-accent)] text-[12px] font-semibold text-gray-600 px-4 py-1.5 transition-colors disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${busy === 'sync' ? 'animate-spin' : ''}`} /> {isEs ? 'Sincronizar' : 'Sync now'}
              </button>
              <button onClick={disconnect} disabled={!!busy} data-testid="gbp-disconnect-btn" title="Disconnect"
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-red-400 hover:text-red-500 text-[12px] font-semibold text-gray-500 px-4 py-1.5 transition-colors disabled:opacity-50">
                <Unplug className="w-3.5 h-3.5" /> {isEs ? 'Desconectar' : 'Disconnect'}
              </button>
            </>
          )}
        </div>
      </div>

      {err && (
        <p className="mt-3 text-[12.5px] rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2" data-testid="gbp-error">
          {err === 'approval'
            ? (isEs ? '⏳ Google aún no aprobó el acceso del proyecto a la Business Profile API (formulario enviado — tarda días/semanas). La conexión quedó guardada; reintenta cuando llegue la aprobación.' : '⏳ Google has not yet approved this project for the Business Profile API (form submitted — takes days/weeks). Your connection is saved; retry once approved.')
            : `${isEs ? 'Error' : 'Error'}: ${err}`}
        </p>
      )}

      {accounts && (
        <div className="mt-4" data-testid="gbp-account-picker">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">{isEs ? '1 · Elige la cuenta' : '1 · Pick the account'}</p>
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <button key={a.name} onClick={() => loadLocations(a.name)}
                className={`rounded-lg border px-3 py-2 text-[12.5px] font-semibold transition-colors ${account === a.name ? 'border-[var(--rest-accent)] text-[var(--rest-accent)]' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                {a.accountName || a.name} <span className="text-gray-400 font-normal">({a.type})</span>
              </button>
            ))}
            {!accounts.length && <p className="text-[12.5px] text-gray-500">{isEs ? 'Sin cuentas visibles en este Google account.' : 'No accounts visible for this Google account.'}</p>}
          </div>
        </div>
      )}
      {locations && (
        <div className="mt-4" data-testid="gbp-location-picker">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">{isEs ? '2 · Elige la ubicación' : '2 · Pick the location'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {locations.map((l) => (
              <button key={l.name} onClick={() => select(l)} disabled={busy === 'sync'}
                className="flex items-start gap-2 rounded-lg border border-gray-200 hover:border-[var(--rest-accent)] px-3 py-2 text-left transition-colors disabled:opacity-50">
                <MapPin className="w-4 h-4 text-[var(--rest-accent)] mt-0.5 shrink-0" />
                <span>
                  <span className="block text-[13px] font-semibold text-gray-900">{l.title}</span>
                  <span className="block text-[11.5px] text-gray-400">{l.address}</span>
                </span>
              </button>
            ))}
            {!locations.length && <p className="text-[12.5px] text-gray-500">{isEs ? 'Sin ubicaciones en esta cuenta.' : 'No locations in this account.'}</p>}
          </div>
        </div>
      )}
      <p className="mt-3 text-[10.5px] text-gray-400">
        {isEs ? 'Fuente: Google Business Profile API · tokens cifrados en servidor, nunca expuestos al navegador.' : 'Source: Google Business Profile API · tokens encrypted server-side, never exposed to the browser.'}
      </p>
    </RCard>
  );
}
